import { createElement, lazy, useState, type ComponentType } from "react";

type Loaded<T> = Promise<T> & { status?: string; value?: T };

/** #1191: one promise per chunk, marked fulfilled once loaded, so `lazyPage` can tell. */
export function once<T>(load: () => Promise<T>) {
  let p: Loaded<T> | undefined;
  return () => {
    if (!p) {
      const q: Loaded<T> = load();
      q.then((m) => { q.status = "fulfilled"; q.value = m; }, () => {});
      p = q;
    }
    return p;
  };
}

/**
 * #1191: React.lazy suspends on its first render even when its chunk loaded long before, and the
 * page's effects (its data requests) then wait for React's retry, about 300 ms after sign-in.
 * A page whose chunk has loaded renders the module directly; otherwise it goes through lazy.
 * `get` must come from `once`.
 */
export function lazyPage(get: () => Promise<{ default: ComponentType }>) {
  const Lazy = lazy(get);
  return function LazyPage() {
    // The type is fixed per mount, so a chunk that loads mid-mount never remounts the page.
    const [Page] = useState<ComponentType>(() => {
      const p: Loaded<{ default: ComponentType }> = get();
      return p.status === "fulfilled" && p.value ? p.value.default : Lazy;
    });
    return createElement(Page);
  };
}
